export const adminLocaleCookie = "admin_locale";
export const defaultLocale = "cs";
export const locales = ["cs", "en", "de", "pl", "sk", "es", "hu", "ro", "it", "fr", "sv", "nl", "el", "ru", "zh", "hi", "id", "ja", "bg", "pt-PT", "pt-BR", "uk", "da", "nb", "hr", "fi", "tr", "ar", "bn", "vi", "th", "ko", "sw", "sl", "sr", "ga", "lt", "sq", "lv", "et", "mt", "ca", "ms", "fil", "am"] as const;

export type AdminLocale = (typeof locales)[number];

export function isAdminLocale(locale: unknown): locale is AdminLocale {
  return typeof locale === "string" && locales.includes(locale as AdminLocale);
}

export function resolveAdminLocale(locale: unknown): AdminLocale {
  if (isAdminLocale(locale)) return locale;
  if (typeof locale === "string") {
    const normalized = locale.trim().toLowerCase().replaceAll("_", "-");
    if (normalized === "pt" || normalized === "pt-pt") return "pt-PT";
    if (normalized === "pt-br") return "pt-BR";
    if (["nb", "nb-no", "no", "no-no"].includes(normalized)) return "nb";
    if (normalized === "hr-hr") return "hr";
    if (normalized === "fi-fi") return "fi";
    if (normalized === "tr-tr") return "tr";
    if (normalized.startsWith("ar-")) return "ar";
    if (normalized === "bn" || normalized.startsWith("bn-")) return "bn";
    if (normalized === "vi" || normalized.startsWith("vi-")) return "vi";
    if (normalized === "th" || normalized.startsWith("th-")) return "th";
    if (normalized === "ko" || normalized.startsWith("ko-")) return "ko";
    if (normalized === "sw" || normalized.startsWith("sw-")) return "sw";
    if (normalized === "sl" || normalized.startsWith("sl-")) return "sl";
    if (normalized === "sr" || normalized.startsWith("sr-")) return "sr";
    if (normalized === "ga" || normalized.startsWith("ga-")) return "ga";
    if (normalized === "lt" || normalized.startsWith("lt-")) return "lt";
    if (normalized === "sq" || normalized.startsWith("sq-")) return "sq";
    if (normalized === "lv" || normalized.startsWith("lv-")) return "lv";
    if (normalized === "et" || normalized.startsWith("et-")) return "et";
    if (normalized === "mt" || normalized.startsWith("mt-")) return "mt";
    if (normalized === "ca" || normalized.startsWith("ca-")) return "ca";
    if (normalized === "ms" || normalized.startsWith("ms-")) return "ms";
    if (normalized === "am" || normalized.startsWith("am-")) return "am";
    if (normalized === "fil" || normalized.startsWith("fil-") || normalized === "tl" || normalized.startsWith("tl-")) return "fil";
  }
  return defaultLocale;
}

const formattingLocales: Record<AdminLocale, string> = {
  cs: "cs-CZ",
  en: "en-US",
  de: "de-DE",
  pl: "pl-PL",
  sk: "sk-SK",
  es: "es-ES",
  hu: "hu-HU",
  ro: "ro-RO",
  it: "it-IT",
  fr: "fr-FR",
  sv: "sv-SE",
  nl: "nl-NL",
  el: "el-GR",
  ru: "ru-RU",
  zh: "zh-CN",
  hi: "hi-IN",
  id: "id-ID",
  ja: "ja-JP",
  bg: "bg-BG",
  "pt-PT": "pt-PT",
  "pt-BR": "pt-BR",
  uk: "uk-UA",
  da: "da-DK",
  nb: "nb-NO",
  hr: "hr-HR",
  fi: "fi-FI",
  tr: "tr-TR",
  ar: "ar",
  bn: "bn-BD",
  vi: "vi-VN",
  th: "th-TH",
  ko: "ko-KR",
  sw: "sw-TZ",
  sl: "sl-SI",
  sr: "sr-Cyrl-RS",
  ga: "ga-IE",
  lt: "lt-LT",
  sq: "sq-AL",
  lv: "lv-LV",
  et: "et-EE",
  mt: "mt-MT",
  ca: "ca-ES",
  ms: "ms-MY",
  fil: "fil-PH",
  am: "am-ET",
};

export function adminFormattingLocale(
  locale: unknown,
  englishLocale: "en-US" | "en-GB" = "en-US",
): string {
  const resolvedLocale = resolveAdminLocale(locale);
  return resolvedLocale === "en" ? englishLocale : formattingLocales[resolvedLocale];
}
